// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Pencil, UserX, CalendarPlus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { toast } from "sonner";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

// Permission keys MUST match what the Staff Portal checks with .includes()
const PERMISSION_OPTIONS = [
  { key: "check_in", label: "Check-In" },
  { key: "score_matches", label: "Live Scoring" },
  { key: "manage_event", label: "Manage" },
];

const StaffManagementPage: React.FC = () => {
  const { scopeOrgId, createOrgId, ready } = useAdminOrgScope();

  const [staff, setStaff] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Add/Edit staff dialog
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "" });
  const [saving, setSaving] = useState(false);

  // Assignment dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignStaff, setAssignStaff] = useState<any | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [courts, setCourts] = useState<any[]>([]);
  const [assignForm, setAssignForm] = useState({
    eventId: "",
    role: "",
    courtId: "",
    permissions: ["check_in"] as string[],
  });
  const [assignSaving, setAssignSaving] = useState(false);

  const loadStaff = async () => {
    setLoading(true);
    try {
      let q = supabase
        .from("event_staff")
        .select("id, name, email, phone, role, is_active, user_id, organization_id")
        .eq("is_active", true)
        .order("name");
      if (scopeOrgId) q = q.eq("organization_id", scopeOrgId);
      const { data, error } = await q;
      if (error) throw error;

      // Assignment counts
      const ids = (data || []).map((s: any) => s.id);
      let counts: Record<string, number> = {};
      if (ids.length) {
        const { data: asg } = await supabase
          .from("event_staff_assignments")
          .select("id, staff_id")
          .in("staff_id", ids);
        (asg || []).forEach((a: any) => {
          counts[a.staff_id] = (counts[a.staff_id] || 0) + 1;
        });
      }
      setStaff((data || []).map((s: any) => ({ ...s, assignment_count: counts[s.id] || 0 })));
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to load staff");
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      let q = supabase
        .from("events")
        .select("id, name, start_date, organization_id")
        .order("start_date", { ascending: false });
      if (scopeOrgId) q = q.eq("organization_id", scopeOrgId);
      const { data, error } = await q;
      if (error) throw error;
      setEvents(data || []);
    } catch (e: any) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!ready) return;
    loadStaff();
    loadEvents();
  }, [ready, scopeOrgId]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", email: "", phone: "", role: "" });
    setStaffDialogOpen(true);
  };

  const openEdit = (s: any) => {
    setEditing(s);
    setForm({ name: s.name || "", email: s.email || "", phone: s.phone || "", role: s.role || "" });
    setStaffDialogOpen(true);
  };

  const saveStaff = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      // Try to link to an existing profile by email
      let userId: string | null = editing?.user_id || null;
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .ilike("email", form.email.trim())
        .maybeSingle();
      if (profile?.id) userId = profile.id;

      if (editing) {
        const { error } = await supabase
          .from("event_staff")
          .update({
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || null,
            role: form.role.trim() || null,
            user_id: userId,
          })
          .eq("id", editing.id);
        if (error) throw error;
        toast.success("Staff member updated");
      } else {
        const { error } = await supabase.from("event_staff").insert({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          role: form.role.trim() || null,
          organization_id: createOrgId,
          is_active: true,
          user_id: userId,
        });
        if (error) throw error;
        toast.success(userId ? "Staff added & linked to existing user" : "Staff member added");
      }
      setStaffDialogOpen(false);
      loadStaff();
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to save staff");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (s: any) => {
    if (!confirm(`Deactivate ${s.name}? They will lose Staff Portal access.`)) return;
    try {
      const { error } = await supabase
        .from("event_staff")
        .update({ is_active: false })
        .eq("id", s.id);
      if (error) throw error;
      toast.success("Staff member deactivated");
      loadStaff();
    } catch (e: any) {
      toast.error("Failed to deactivate");
    }
  };

  const openAssign = async (s: any) => {
    setAssignStaff(s);
    setAssignForm({ eventId: "", role: s.role || "", courtId: "", permissions: ["check_in"] });
    setCourts([]);
    setAssignOpen(true);
    await loadAssignments(s.id);
  };

  const loadAssignments = async (staffId: string) => {
    const { data, error } = await supabase
      .from("event_staff_assignments")
      .select("id, event_id, role, permissions, court_id, events:event_id(name)")
      .eq("staff_id", staffId);
    if (error) {
      console.error(error);
      return;
    }
    setAssignments(data || []);
  };

  const loadCourts = async (eventId: string) => {
    if (!eventId) {
      setCourts([]);
      return;
    }
    const { data } = await supabase
      .from("event_courts")
      .select("id, name")
      .eq("event_id", eventId)
      .eq("is_active", true)
      .order("sort_order");
    setCourts(data || []);
  };

  const togglePerm = (key: string) => {
    setAssignForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  };

  const saveAssignment = async () => {
    if (!assignForm.eventId) {
      toast.error("Select an event");
      return;
    }
    if (assignForm.permissions.length === 0) {
      toast.error("Select at least one permission");
      return;
    }
    setAssignSaving(true);
    try {
      const { error } = await supabase.from("event_staff_assignments").insert({
        staff_id: assignStaff.id,
        event_id: assignForm.eventId,
        role: assignForm.role.trim() || null,
        // CRITICAL: store as a JSON ARRAY, not an object
        permissions: assignForm.permissions,
        court_id: assignForm.courtId || null,
      });
      if (error) throw error;
      toast.success("Assignment added");
      setAssignForm((f) => ({ ...f, eventId: "", courtId: "", permissions: ["check_in"] }));
      setCourts([]);
      await loadAssignments(assignStaff.id);
      loadStaff();
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to add assignment");
    } finally {
      setAssignSaving(false);
    }
  };

  const removeAssignment = async (id: string) => {
    try {
      const { error } = await supabase.from("event_staff_assignments").delete().eq("id", id);
      if (error) throw error;
      toast.success("Assignment removed");
      await loadAssignments(assignStaff.id);
      loadStaff();
    } catch (e: any) {
      toast.error("Failed to remove assignment");
    }
  };

  const permLabel = (key: string) =>
    PERMISSION_OPTIONS.find((p) => p.key === key)?.label || key;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-700">Staff</h1>
          <p className="text-muted-foreground">
            Manage event staff and assign them to events for Check-In and Live Scoring.
          </p>
        </div>
        <Button onClick={openAdd}>
          <Plus className="h-4 w-4 mr-2" /> Add staff
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Staff Directory</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Portal Access</TableHead>
                    <TableHead>Assignments</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staff.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No staff yet. Click "Add staff" to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    staff.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell>{s.email}</TableCell>
                        <TableCell>{s.phone || "—"}</TableCell>
                        <TableCell>{s.role || "—"}</TableCell>
                        <TableCell>
                          {s.user_id ? (
                            <Badge variant="success">Linked</Badge>
                          ) : (
                            <Badge variant="secondary">No login</Badge>
                          )}
                        </TableCell>
                        <TableCell>{s.assignment_count}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button variant="outline" size="sm" onClick={() => openAssign(s)}>
                            <CalendarPlus className="h-4 w-4 mr-1" /> Assign
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(s)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deactivate(s)}>
                            <UserX className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit staff dialog */}
      <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                If this email matches an existing user, they'll be linked for Staff Portal login.
              </p>
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+1..."
              />
            </div>
            <div>
              <Label>Role</Label>
              <Input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="Scorekeeper, Coordinator..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStaffDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveStaff} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Event Assignments — {assignStaff?.name}</DialogTitle>
          </DialogHeader>

          {/* Existing assignments */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Current Assignments</h4>
            {assignments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No assignments yet.</p>
            ) : (
              <div className="space-y-2">
                {assignments.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between rounded-md border p-2"
                  >
                    <div>
                      <p className="font-medium text-sm">{a.events?.name || "Event"}</p>
                      <div className="flex gap-1 flex-wrap mt-1">
                        {(Array.isArray(a.permissions) ? a.permissions : []).map((p: string) => (
                          <Badge key={p} variant="secondary" className="text-xs">
                            {permLabel(p)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeAssignment(a.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* New assignment form */}
          <div className="space-y-4 border-t pt-4">
            <h4 className="text-sm font-semibold">Add Assignment</h4>
            <div>
              <Label>Event *</Label>
              <Select
                value={assignForm.eventId}
                onValueChange={(v) => {
                  setAssignForm({ ...assignForm, eventId: v, courtId: "" });
                  loadCourts(v);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                      {e.start_date ? ` (${format(parseLocalDate(e.start_date), "MMM d, yyyy")})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role</Label>
                <Input
                  value={assignForm.role}
                  onChange={(e) => setAssignForm({ ...assignForm, role: e.target.value })}
                  placeholder="Scorekeeper..."
                />
              </div>
              <div>
                <Label>Court (optional)</Label>
                <Select
                  value={assignForm.courtId || "none"}
                  onValueChange={(v) =>
                    setAssignForm({ ...assignForm, courtId: v === "none" ? "" : v })
                  }
                  disabled={courts.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={courts.length ? "Any court" : "No courts"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any court</SelectItem>
                    {courts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Permissions *</Label>
              <div className="flex gap-4 mt-2">
                {PERMISSION_OPTIONS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={assignForm.permissions.includes(p.key)}
                      onCheckedChange={() => togglePerm(p.key)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={saveAssignment} disabled={assignSaving} className="w-full">
              {assignSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Assignment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagementPage;
